import React from 'react';

import {StructuredTableEditStatus} from 'notebook/structured-tables/action-functions/toggle-edit-mode/reduceToggledTableEditState';
import {
  TableActionFunctionParameters,
  ToolbarButtonActionFunction,
} from 'notebook/structured-tables/StructuredTableConstants';
import isTableActionButtonDisabled from 'notebook/structured-tables/util/isTableActionButtonDisabled';
import tableActionButtonDisabledTooltip from 'notebook/structured-tables/util/tableActionButtonDisabledTooltip';
import {ButtonStyle} from 'ui-library/buttons/Button';

import ActionFunctionButton from '../ActionFunctionButton';
import {
  getStructuredTableTogglableStatus,
  setStructuredTableTogglableStatus,
} from './StructuredTableTogglableStateActions';

/**
 * Utility for creating an action function which manages a structured table with a togglable
 * edit mode. Tightly coupled with StructuredTableEditStatusManager
 */

export default function makeToggleEditModeActionFunction<
  TTableMetadata,
  TRowMetadata,
>(): ToolbarButtonActionFunction<TTableMetadata, TRowMetadata> {
  return {
    renderToolbarButton: (params: TableActionFunctionParameters<TTableMetadata, TRowMetadata>) => {
      const {structuredTable, locked: tableIsLocked, isCollapsed} = params;
      const currentStatus = getStructuredTableTogglableStatus(structuredTable.cid);
      const tooltipText = tableActionButtonDisabledTooltip(params);

      if (isCollapsed) {
        return null;
      }

      if (currentStatus === StructuredTableEditStatus.LOCKED) {
        return (
          <ActionFunctionButton
            tooltip={tooltipText || ''}
            disabled={tableIsLocked}
            icon="edit"
            iconAltText="Edit table"
            buttonText="Edit"
            onClick={() => {
              setStructuredTableTogglableStatus(structuredTable.cid, StructuredTableEditStatus.EDITING);
            }}
          />
        );
      } else {
        const saving = currentStatus === StructuredTableEditStatus.SAVING;
        const reverting = currentStatus === StructuredTableEditStatus.REVERTING;
        const showCancelButton = !saving && !reverting;
        const submitButtonText = saving ? 'Submitting...' : 'Submit';
        return (
          <>
            {showCancelButton && (
              <ActionFunctionButton
                tooltip=""
                buttonStyle={ButtonStyle.TERTIARY_GRAY}
                disabled={false}
                iconAltText=""
                buttonText="Cancel"
                onClick={() => {
                  setStructuredTableTogglableStatus(structuredTable.cid, StructuredTableEditStatus.REVERTING);
                }}
              />
            )}
            <ActionFunctionButton
              tooltip={tooltipText || ''}
              buttonStyle={ButtonStyle.SECONDARY}
              disabled={isTableActionButtonDisabled(params) || saving || reverting}
              iconAltText={submitButtonText}
              buttonText={submitButtonText}
              onClick={() => {
                setStructuredTableTogglableStatus(structuredTable.cid, StructuredTableEditStatus.SAVING);
              }}
            />
          </>
        );
      }
    },
  };
}
